import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Building2,
  Sparkles,
  Palette,
  Image as ImageIcon,
  Play,
  Loader2,
  Check,
  Plus,
  Trash2,
  Wand2,
  Download,
  RefreshCw,
  Eye,
  Edit3,
  ScanSearch,
  Type,
  Layers,
  Zap,
  Target,
  Award,
  TrendingUp,
  MinusCircle,
  Upload,
  Globe,
  Grid3X3,
  ImagePlus,
  Save,
  FolderOpen,
  BookMarked,
  Briefcase as BriefcaseIcon
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Layout style options
const LAYOUT_STYLES = [
  { 
    id: "professional", 
    label: "Professionell", 
    description: "Seriös und vertrauenswürdig", 
    icon: Award,
    preview: "bg-white border-gray-200"
  },
  { 
    id: "viral", 
    label: "Viral / Auffällig", 
    description: "Dunkel, kontrastreich, scroll-stoppend", 
    icon: Zap,
    preview: "bg-gray-900 border-yellow-400"
  },
  { 
    id: "minimal", 
    label: "Minimalistisch", 
    description: "Clean und elegant", 
    icon: Layers,
    preview: "bg-gray-50 border-gray-300"
  }
];

// Format options
const FORMAT_OPTIONS = [
  { id: "1080x1080", label: "Square (1:1)", dimensions: "1080×1080", description: "Instagram Feed" },
  { id: "1080x1920", label: "Story (9:16)", dimensions: "1080×1920", description: "Stories & Reels" }
];

// Wizard steps
const STEPS = [
  { id: 1, title: "Grunddaten", icon: Briefcase, description: "Job & Firma" },
  { id: 2, title: "Vorteile", icon: TrendingUp, description: "Deine Stärken" },
  { id: 3, title: "Design", icon: Palette, description: "Layout & Style" },
  { id: 4, title: "Extras", icon: ImagePlus, description: "Logo & Details" },
  { id: 5, title: "Generieren", icon: Play, description: "Bilder erstellen" }
];

// Logo position options
const LOGO_POSITIONS = [
  { id: "top-left", label: "Oben links" },
  { id: "top-right", label: "Oben rechts" },
  { id: "bottom-left", label: "Unten links" },
  { id: "bottom-right", label: "Unten rechts" },
  { id: "ai_decides", label: "KI entscheidet" }
];

// Emoji options for advantages
const EMOJI_OPTIONS = [
  { id: "check", icon: "✓", label: "Standard Haken" },
  { id: "money", icon: "💰", label: "Gehalt/Geld" },
  { id: "vacation", icon: "🏖️", label: "Urlaub" },
  { id: "clock", icon: "⏰", label: "Arbeitszeit" },
  { id: "education", icon: "📚", label: "Weiterbildung" },
  { id: "heart", icon: "❤️", label: "Wertschätzung" },
  { id: "team", icon: "👥", label: "Team" },
  { id: "star", icon: "⭐", label: "Karriere" },
  { id: "car", icon: "🚗", label: "Firmenwagen" },
  { id: "home", icon: "🏠", label: "Home Office" }
];

export const ComparisonAdGenerator = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [generatingDisadvantages, setGeneratingDisadvantages] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]); // Multiple images now!
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [disadvantagesGenerated, setDisadvantagesGenerated] = useState(false);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Job tracking
  const [currentJobId, setCurrentJobId] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  
  // NEW: Logos from library
  const [logos, setLogos] = useState([]);
  const [loadingLogos, setLoadingLogos] = useState(false);
  
  // NEW: Reference image
  const [referenceImage, setReferenceImage] = useState(null);
  const [referenceImagePreview, setReferenceImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // Text editing state
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [detectedTexts, setDetectedTexts] = useState([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [selectedText, setSelectedText] = useState(null);
  const [replacementText, setReplacementText] = useState("");
  const [isReplacing, setIsReplacing] = useState(false);
  
  // Form data - EXTENDED with new fields
  const [formData, setFormData] = useState({
    jobTitle: "",
    companyName: "",
    companyColor: "#4CAF50",
    companyWebsite: "",
    advantages: ["", "", "", ""],
    disadvantages: ["", "", "", ""],
    advantageEmojis: ["check", "check", "check", "check"], // Emoji for each advantage
    layoutStyle: "professional",
    formats: ["1080x1080"],
    ctaText: "Jetzt bewerben!",
    ctaSubtext: "Ohne Lebenslauf & Anschreiben",
    selectedLogoId: null,
    logoPosition: "bottom-right",
    variationCount: 2,
    useReferenceImage: false
  });

  // Load logos and templates on mount
  useEffect(() => {
    loadLogos();
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await axios.get(`${API}/comparison-templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Bitte Vorlagennamen eingeben");
      return;
    }
    
    setSavingTemplate(true);
    try {
      // Get preview image from first generated image if available
      const previewImage = generatedImages.length > 0 ? generatedImages[0]?.image : null;
      
      const response = await axios.post(`${API}/comparison-templates`, {
        name: templateName,
        form_state: formData,
        preview_image: previewImage
      });
      
      if (response.data.success) {
        toast.success(`Vorlage "${templateName}" gespeichert!`);
        setShowSaveTemplateDialog(false);
        setTemplateName("");
        loadTemplates();
      }
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Fehler beim Speichern der Vorlage");
    } finally {
      setSavingTemplate(false);
    }
  };

  const loadTemplate = async (templateId) => {
    try {
      const response = await axios.get(`${API}/comparison-templates/${templateId}`);
      const template = response.data;
      
      if (template.form_state) {
        setFormData(template.form_state);
        toast.success(`Vorlage "${template.name}" geladen!`);
        setCurrentStep(1);
      }
    } catch (error) {
      console.error("Error loading template:", error);
      toast.error("Fehler beim Laden der Vorlage");
    }
  };

  const deleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Vorlage "${templateName}" wirklich löschen?`)) return;
    
    try {
      await axios.delete(`${API}/comparison-templates/${templateId}`);
      toast.success("Vorlage gelöscht");
      loadTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Fehler beim Löschen der Vorlage");
    }
  };

  const loadLogos = async () => {
    setLoadingLogos(true);
    try {
      const response = await axios.get(`${API}/logos`);
      setLogos(response.data || []);
    } catch (error) {
      console.error("Error loading logos:", error);
    } finally {
      setLoadingLogos(false);
    }
  };

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const updateAdvantage = (index, value) => {
    setFormData(prev => {
      const newAdvantages = [...prev.advantages];
      newAdvantages[index] = value;
      return { ...prev, advantages: newAdvantages };
    });
  };

  const updateDisadvantage = (index, value) => {
    setFormData(prev => {
      const newDisadvantages = [...prev.disadvantages];
      newDisadvantages[index] = value;
      return { ...prev, disadvantages: newDisadvantages };
    });
  };

  const addAdvantage = () => {
    if (formData.advantages.length < 6) {
      setFormData(prev => ({
        ...prev,
        advantages: [...prev.advantages, ""],
        disadvantages: [...prev.disadvantages, ""],
        advantageEmojis: [...prev.advantageEmojis, "check"]
      }));
    }
  };

  const removeAdvantage = (index) => {
    if (formData.advantages.length > 2) {
      setFormData(prev => ({
        ...prev,
        advantages: prev.advantages.filter((_, i) => i !== index),
        disadvantages: prev.disadvantages.filter((_, i) => i !== index),
        advantageEmojis: prev.advantageEmojis.filter((_, i) => i !== index)
      }));
    }
  };

  const updateAdvantageEmoji = (index, emojiId) => {
    setFormData(prev => {
      const newEmojis = [...prev.advantageEmojis];
      newEmojis[index] = emojiId;
      return { ...prev, advantageEmojis: newEmojis };
    });
  };

  // NEW: Handle reference image upload
  const handleReferenceImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Bitte nur Bilddateien hochladen");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(",")[1];
      setReferenceImage(base64);
      setReferenceImagePreview(event.target.result);
      updateFormData("useReferenceImage", true);
      toast.success("Referenzbild hochgeladen!");
    };
    reader.readAsDataURL(file);
  };

  const removeReferenceImage = () => {
    setReferenceImage(null);
    setReferenceImagePreview(null);
    updateFormData("useReferenceImage", false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Auto-generate disadvantages using AI
  const generateDisadvantages = async () => {
    const filledAdvantages = formData.advantages.filter(a => a.trim());
    if (filledAdvantages.length === 0) {
      toast.error("Bitte mindestens einen Vorteil eingeben");
      return;
    }

    setGeneratingDisadvantages(true);
    try {
      const response = await axios.post(`${API}/comparison-ads/generate-disadvantages`, {
        advantages: filledAdvantages,
        job_title: formData.jobTitle || "Mitarbeiter"
      });

      if (response.data.success && response.data.disadvantages) {
        const newDisadvantages = [...formData.disadvantages];
        response.data.disadvantages.forEach((dis, i) => {
          if (i < newDisadvantages.length) {
            newDisadvantages[i] = dis;
          }
        });
        updateFormData("disadvantages", newDisadvantages);
        setDisadvantagesGenerated(true);
        toast.success("Nachteile erfolgreich generiert!");
      } else {
        toast.error(response.data.error || "Fehler beim Generieren der Nachteile");
      }
    } catch (error) {
      console.error("Error generating disadvantages:", error);
      toast.error("Fehler beim Generieren der Nachteile");
    } finally {
      setGeneratingDisadvantages(false);
    }
  };

  // Generate the comparison ad image - NOW WITH MULTIPLE VARIATIONS AND JOB TRACKING
  const generateAd = async () => {
    const filledAdvantages = formData.advantages.filter(a => a.trim());
    if (filledAdvantages.length < 2) {
      toast.error("Bitte mindestens 2 Vorteile eingeben");
      return;
    }

    if (!formData.jobTitle.trim()) {
      toast.error("Bitte Berufsbezeichnung eingeben");
      return;
    }

    if (!formData.companyName.trim()) {
      toast.error("Bitte Firmenname eingeben");
      return;
    }

    setGenerating(true);
    setGeneratedImages([]);
    setSelectedImageIndex(0);
    setGenerationProgress(0);
    
    try {
      const filledDisadvantages = formData.disadvantages.filter((d, i) => d.trim() && i < filledAdvantages.length);
      const variationCount = formData.variationCount || 2;
      
      // Create job for tracking
      let jobId = null;
      try {
        const jobResponse = await axios.post(`${API}/generation-jobs`, {
          job_type: "comparison_ad",
          template_name: `${formData.companyName} - ${formData.jobTitle}`,
          settings: {
            jobTitle: formData.jobTitle,
            companyName: formData.companyName,
            formats: formData.formats,
            layoutStyle: formData.layoutStyle
          },
          requested_count: variationCount * (formData.formats?.length || 1)
        });
        jobId = jobResponse.data.job_id;
        setCurrentJobId(jobId);
        
        // Update job status to in_progress
        await axios.patch(`${API}/generation-jobs/${jobId}`, {
          status: "in_progress"
        });
      } catch (err) {
        console.warn("Could not create job:", err);
      }
      
      // Get selected logo data - fetch full logo with image_data
      let logoData = null;
      if (formData.selectedLogoId) {
        try {
          const logoResponse = await axios.get(`${API}/logos/${formData.selectedLogoId}/full`);
          if (logoResponse.data?.image_data) {
            logoData = logoResponse.data.image_data;
          }
        } catch (err) {
          console.warn("Could not load logo data:", err);
        }
      }
      
      // Generate variations for each selected format
      const variations = [];
      const selectedFormats = formData.formats || ["1080x1080"];
      const totalGenerations = variationCount * selectedFormats.length;
      
      toast.info(`Generiere ${totalGenerations} Bilder (${selectedFormats.length} Format(e) × ${variationCount} Variationen)...`);
      
      let generationIndex = 0;
      for (const currentFormat of selectedFormats) {
        for (let i = 0; i < variationCount; i++) {
          try {
            generationIndex++;
            setGenerationProgress(Math.round(((generationIndex - 1) / totalGenerations) * 100));
            
            // Get emojis only for filled advantages
            const filledEmojis = formData.advantageEmojis.filter((_, idx) => formData.advantages[idx]?.trim());
            
            const response = await axios.post(`${API}/comparison-ads/generate`, {
              job_title: formData.jobTitle,
              company_name: formData.companyName,
              company_color: formData.companyColor,
              company_website: formData.companyWebsite || null,
              advantages: filledAdvantages,
              disadvantages: filledDisadvantages.length > 0 ? filledDisadvantages : null,
              advantage_emojis: filledEmojis,
              cta_text: formData.ctaText,
              cta_subtext: formData.ctaSubtext,
              layout_style: formData.layoutStyle,
              format: currentFormat,
              logo_id: formData.selectedLogoId,
              logo_data: logoData,
              logo_position: formData.logoPosition,
              reference_image: referenceImage,
              variation_index: i + 1
            });

          if (response.data.success && response.data.image) {
            const formatLabel = currentFormat === "1080x1920" ? "Story" : "Square";
            const newImage = {
              image: response.data.image,
              index: generationIndex,
              format: currentFormat,
              label: `${formatLabel} #${i + 1}`
            };
            variations.push(newImage);
            
            // Update UI progressively
            setGeneratedImages(prev => [...prev, newImage]);
            
            // Save image to job
            if (jobId) {
              try {
                await axios.post(`${API}/generation-jobs/${jobId}/add-image`, {
                  image_data: response.data.image
                });
              } catch (err) {
                console.warn("Could not add image to job:", err);
              }
            }
            
            toast.success(`${formatLabel} Variation ${i + 1} erstellt!`);
          }
        } catch (error) {
          console.error(`Error generating variation ${generationIndex}:`, error);
        }
        
        setGenerationProgress(Math.round((generationIndex / totalGenerations) * 100));
      }
      }

      // Update job status
      if (jobId) {
        try {
          await axios.patch(`${API}/generation-jobs/${jobId}`, {
            status: variations.length > 0 ? "completed" : "failed",
            error_message: variations.length === 0 ? "Keine Bilder generiert" : null
          });
        } catch (err) {
          console.warn("Could not update job status:", err);
        }
      }

      if (variations.length > 0) {
        toast.success(
          <div>
            <p>{variations.length} Vergleichs-Ads erstellt!</p>
            <p className="text-xs mt-1">Gespeichert unter "Meine Aufträge"</p>
          </div>
        );
      } else {
        toast.error("Keine Bilder konnten generiert werden");
      }
    } catch (error) {
      console.error("Error generating ads:", error);
      toast.error("Fehler bei der Generierung: " + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
      setGenerationProgress(100);
    }
  };

  // Get current selected image
  const currentImage = generatedImages[selectedImageIndex]?.image || null;

  // Detect text in generated image
  const detectText = async () => {
    if (!currentImage) return;
    
    setIsDetecting(true);
    try {
      const response = await axios.post(`${API}/editor/detect-text`, {
        image_data: currentImage
      });

      if (response.data.texts && response.data.texts.length > 0) {
        setDetectedTexts(response.data.texts);
        toast.success(`${response.data.texts.length} Text(e) erkannt`);
      } else {
        setDetectedTexts([]);
        toast.info("Kein Text im Bild erkannt");
      }
    } catch (error) {
      console.error("Text detection error:", error);
      toast.error("Fehler bei der Texterkennung");
    } finally {
      setIsDetecting(false);
    }
  };

  // Replace text using AI
  const replaceText = async () => {
    if (!selectedText || !replacementText.trim()) {
      toast.error("Bitte Text auswählen und neuen Text eingeben");
      return;
    }

    if (!currentImage) return;

    setIsReplacing(true);
    try {
      const response = await axios.post(`${API}/editor/replace-text`, {
        image_data: currentImage,
        original_text: selectedText.text,
        new_text: replacementText
      });

      if (response.data.success && response.data.image_data) {
        // Update the current image in the array
        setGeneratedImages(prev => {
          const updated = [...prev];
          updated[selectedImageIndex] = {
            ...updated[selectedImageIndex],
            image: response.data.image_data
          };
          return updated;
        });
        setSelectedText(null);
        setReplacementText("");
        setDetectedTexts([]);
        toast.success(`Text erfolgreich ersetzt!`);
      } else {
        toast.error(response.data.error || "KI konnte den Text nicht ersetzen");
      }
    } catch (error) {
      console.error("Text replacement error:", error);
      toast.error("Fehler bei der KI-Textbearbeitung");
    } finally {
      setIsReplacing(false);
    }
  };

  // Download image
  const downloadImage = () => {
    if (!currentImage) return;
    const img = generatedImages[selectedImageIndex];
    const formatLabel = img?.format === "1080x1920" ? "Story" : "Square";
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${currentImage}`;
    link.download = `Vergleich_${formatLabel}_${selectedImageIndex + 1}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Bild heruntergeladen!");
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.jobTitle.trim() && formData.companyName.trim();
      case 2:
        return formData.advantages.filter(a => a.trim()).length >= 2;
      case 3:
        return formData.layoutStyle;
      case 4:
        return formData.ctaText.trim();
      default:
        return true;
    }
  };

  const progress = (currentStep / STEPS.length) * 100;

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6" data-testid="step-basic-info">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2">Grunddaten eingeben</h2>
              <p className="text-muted-foreground">Für welchen Job und welche Firma ist die Anzeige?</p>
            </div>

            {/* Templates Section */}
            {templates.length > 0 && (
              <Card className="max-w-md mx-auto mb-6 bg-muted/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <BookMarked className="w-4 h-4" />
                    Gespeicherte Vorlagen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-24">
                    <div className="space-y-2">
                      {templates.map((template) => (
                        <div 
                          key={template.id} 
                          className="flex items-center justify-between p-2 bg-background rounded-lg border hover:border-primary cursor-pointer"
                        >
                          <div 
                            className="flex-1 truncate"
                            onClick={() => loadTemplate(template.id)}
                          >
                            <span className="font-medium text-sm">{template.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              {new Date(template.created_at).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => loadTemplate(template.id)}
                              data-testid={`load-template-${template.id}`}
                            >
                              <FolderOpen className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              onClick={() => deleteTemplate(template.id, template.name)}
                              data-testid={`delete-template-${template.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}

            <div className="space-y-4 max-w-md mx-auto">
              <div>
                <Label htmlFor="jobTitle">Berufsbezeichnung *</Label>
                <Input
                  id="jobTitle"
                  placeholder="z.B. Physiotherapeut:in, Erzieher:in, Pflegekraft"
                  value={formData.jobTitle}
                  onChange={(e) => updateFormData("jobTitle", e.target.value)}
                  className="mt-1"
                  data-testid="job-title-input"
                />
              </div>

              <div>
                <Label htmlFor="companyName">Firmenname *</Label>
                <Input
                  id="companyName"
                  placeholder="z.B. RAN, KITA LUNA, MedCare"
                  value={formData.companyName}
                  onChange={(e) => updateFormData("companyName", e.target.value)}
                  className="mt-1"
                  data-testid="company-name-input"
                />
              </div>

              <div>
                <Label htmlFor="companyColor">Firmenfarbe</Label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="color"
                    id="companyColor"
                    value={formData.companyColor}
                    onChange={(e) => updateFormData("companyColor", e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer border"
                    data-testid="company-color-input"
                  />
                  <Input
                    value={formData.companyColor}
                    onChange={(e) => updateFormData("companyColor", e.target.value)}
                    className="flex-1"
                    placeholder="#4CAF50"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6" data-testid="step-advantages">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2">Vorteile deiner Firma</h2>
              <p className="text-muted-foreground">Was macht dich als Arbeitgeber besonders?</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Advantages Column */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    Vorteile (min. 2)
                  </CardTitle>
                  <CardDescription>Was bietest du besser als andere? Wähle ein passendes Icon!</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {formData.advantages.map((adv, index) => (
                    <div key={index} className="flex items-center gap-2">
                      {/* Emoji Selector */}
                      <Select
                        value={formData.advantageEmojis[index] || "check"}
                        onValueChange={(v) => updateAdvantageEmoji(index, v)}
                      >
                        <SelectTrigger className="w-16 shrink-0" data-testid={`emoji-select-${index}`}>
                          <SelectValue>
                            {EMOJI_OPTIONS.find(e => e.id === (formData.advantageEmojis[index] || "check"))?.icon || "✓"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {EMOJI_OPTIONS.map((emoji) => (
                            <SelectItem key={emoji.id} value={emoji.id}>
                              <span className="flex items-center gap-2">
                                <span className="text-lg">{emoji.icon}</span>
                                <span className="text-sm">{emoji.label}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder={`Vorteil ${index + 1}...`}
                        value={adv}
                        onChange={(e) => updateAdvantage(index, e.target.value)}
                        data-testid={`advantage-input-${index}`}
                      />
                      {formData.advantages.length > 2 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeAdvantage(index)}
                          className="shrink-0"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                  
                  {formData.advantages.length < 6 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addAdvantage}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Vorteil hinzufügen
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Disadvantages Column */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MinusCircle className="w-5 h-5 text-red-500" />
                    Nachteile (alter Arbeitgeber)
                  </CardTitle>
                  <CardDescription>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={generateDisadvantages}
                      disabled={generatingDisadvantages}
                      className="mt-2"
                      data-testid="generate-disadvantages-btn"
                    >
                      {generatingDisadvantages ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generiere...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4 mr-2" />
                          KI generiert automatisch
                        </>
                      )}
                    </Button>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {formData.disadvantages.map((dis, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        placeholder={`Nachteil ${index + 1}...`}
                        value={dis}
                        onChange={(e) => updateDisadvantage(index, e.target.value)}
                        className={disadvantagesGenerated ? "border-green-300 bg-green-50" : ""}
                        data-testid={`disadvantage-input-${index}`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6" data-testid="step-layout">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2">Layout-Stil wählen</h2>
              <p className="text-muted-foreground">Wie soll deine Anzeige wirken?</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {LAYOUT_STYLES.map((style) => (
                <Card
                  key={style.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    formData.layoutStyle === style.id
                      ? "ring-2 ring-primary border-primary"
                      : "hover:border-gray-400"
                  }`}
                  onClick={() => updateFormData("layoutStyle", style.id)}
                  data-testid={`layout-style-${style.id}`}
                >
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-lg border-2 ${style.preview} flex items-center justify-center`}>
                      <style.icon className="w-8 h-8" />
                    </div>
                    <h3 className="font-semibold mb-1">{style.label}</h3>
                    <p className="text-sm text-muted-foreground">{style.description}</p>
                    {formData.layoutStyle === style.id && (
                      <Badge className="mt-3" variant="default">
                        <Check className="w-3 h-3 mr-1" />
                        Ausgewählt
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <Separator className="my-6" />

            <div className="text-center">
              <Label className="text-lg font-semibold mb-4 block">Formate wählen (Mehrfachauswahl)</Label>
              <div className="flex justify-center gap-4 flex-wrap">
                {FORMAT_OPTIONS.map((format) => (
                  <Card
                    key={format.id}
                    className={`cursor-pointer transition-all w-40 ${
                      formData.formats.includes(format.id)
                        ? "ring-2 ring-primary border-primary"
                        : "hover:border-gray-400"
                    }`}
                    onClick={() => {
                      const current = formData.formats || [];
                      const updated = current.includes(format.id)
                        ? current.filter(f => f !== format.id)
                        : [...current, format.id];
                      if (updated.length > 0) updateFormData("formats", updated);
                    }}
                    data-testid={`format-${format.id}`}
                  >
                    <CardContent className="p-4 text-center">
                      <p className="font-semibold">{format.label}</p>
                      <p className="text-xs text-muted-foreground">{format.dimensions}</p>
                      <p className="text-xs text-muted-foreground mt-1">{format.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6" data-testid="step-extras">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2">Extras & Details</h2>
              <p className="text-muted-foreground">Logo, Website und weitere Anpassungen</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Left Column: Logo & Website */}
              <div className="space-y-6">
                {/* Logo Selection */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Firmenlogo
                    </CardTitle>
                    <CardDescription>Wähle ein Logo aus deiner Bibliothek</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {loadingLogos ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin" />
                      </div>
                    ) : logos.length === 0 ? (
                      <div className="text-center py-4 text-muted-foreground">
                        <p>Noch keine Logos vorhanden.</p>
                        <p className="text-sm">Lade Logos in der Logo-Bibliothek hoch.</p>
                      </div>
                    ) : (
                      <>
                        <ScrollArea className="h-32">
                          <div className="grid grid-cols-4 gap-2">
                            {logos.map((logo) => (
                              <div
                                key={logo.id}
                                onClick={() => updateFormData("selectedLogoId", formData.selectedLogoId === logo.id ? null : logo.id)}
                                className={`relative cursor-pointer rounded-lg border-2 p-1 transition-all hover:border-primary ${
                                  formData.selectedLogoId === logo.id 
                                    ? "border-primary bg-primary/10" 
                                    : "border-transparent hover:bg-muted"
                                }`}
                                data-testid={`logo-option-${logo.id}`}
                              >
                                <img
                                  src={`${API}/logos/${logo.id}/image?token=${localStorage.getItem('auth_token')}`}
                                  alt={logo.name}
                                  className="w-full h-12 object-contain rounded"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                {formData.selectedLogoId === logo.id && (
                                  <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                                    <Check className="w-3 h-3" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                        
                        {formData.selectedLogoId && (
                          <div>
                            <Label className="text-sm">Logo-Position</Label>
                            <Select
                              value={formData.logoPosition}
                              onValueChange={(v) => updateFormData("logoPosition", v)}
                            >
                              <SelectTrigger className="mt-1" data-testid="logo-position-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {LOGO_POSITIONS.map((pos) => (
                                  <SelectItem key={pos.id} value={pos.id}>
                                    {pos.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Website URL */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Globe className="w-5 h-5" />
                      Website-URL
                    </CardTitle>
                    <CardDescription>Wird unten im Bild angezeigt</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Input
                      placeholder="www.deine-firma.de"
                      value={formData.companyWebsite}
                      onChange={(e) => updateFormData("companyWebsite", e.target.value)}
                      data-testid="website-url-input"
                    />
                  </CardContent>
                </Card>

                {/* CTA */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      Call-to-Action
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label htmlFor="ctaText">Button-Text</Label>
                      <Input
                        id="ctaText"
                        placeholder="Jetzt bewerben!"
                        value={formData.ctaText}
                        onChange={(e) => updateFormData("ctaText", e.target.value)}
                        className="mt-1"
                        data-testid="cta-text-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ctaSubtext">Subtext</Label>
                      <Input
                        id="ctaSubtext"
                        placeholder="Ohne Lebenslauf & Anschreiben"
                        value={formData.ctaSubtext}
                        onChange={(e) => updateFormData("ctaSubtext", e.target.value)}
                        className="mt-1"
                        data-testid="cta-subtext-input"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column: Reference Image & Variations */}
              <div className="space-y-6">
                {/* Reference Image Upload */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Upload className="w-5 h-5" />
                      Referenzbild (optional)
                    </CardTitle>
                    <CardDescription>
                      Lade ein Beispiel-Bild hoch, an dessen Stil sich die KI orientiert
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleReferenceImageUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    
                    {referenceImagePreview ? (
                      <div className="space-y-3">
                        <div className="relative">
                          <img
                            src={referenceImagePreview}
                            alt="Reference"
                            className="w-full h-40 object-contain rounded-lg border bg-muted"
                          />
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute top-2 right-2"
                            onClick={removeReferenceImage}
                            data-testid="remove-reference-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Badge variant="secondary" className="w-full justify-center">
                          <Check className="w-3 h-3 mr-1" />
                          Referenzbild aktiv
                        </Badge>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors"
                        data-testid="upload-reference-btn"
                      >
                        <ImageIcon className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Klicke zum Hochladen
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          z.B. ein Vergleichs-Ad das dir gefällt
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Variation Count */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Grid3X3 className="w-5 h-5" />
                      Anzahl Variationen
                    </CardTitle>
                    <CardDescription>
                      Wie viele verschiedene Designs sollen erstellt werden?
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold">{formData.variationCount}</span>
                      <span className="text-muted-foreground">Bilder</span>
                    </div>
                    <Slider
                      value={[formData.variationCount]}
                      onValueChange={(v) => updateFormData("variationCount", v[0])}
                      min={1}
                      max={4}
                      step={1}
                      className="w-full"
                      data-testid="variation-slider"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>1</span>
                      <span>2</span>
                      <span>3</span>
                      <span>4</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Preview Summary */}
                <Card className="bg-muted/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Vorschau deiner Einstellungen</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Logo:</span>
                      <span>{formData.selectedLogoId ? logos.find(l => l.id === formData.selectedLogoId)?.name || "Ausgewählt" : "Keins"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Website:</span>
                      <span>{formData.companyWebsite || "Keine"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Referenzbild:</span>
                      <span>{referenceImage ? "✓ Aktiv" : "Keins"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Variationen:</span>
                      <span>{formData.variationCount} Bilder</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6" data-testid="step-generate">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2">Bild generieren</h2>
              <p className="text-muted-foreground">Überprüfe deine Eingaben und erstelle die Anzeige</p>
            </div>

            {/* Summary */}
            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle>Zusammenfassung</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Berufsbezeichnung</p>
                    <p className="font-semibold">{formData.jobTitle}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Firma</p>
                    <p className="font-semibold flex items-center gap-2">
                      <span className="w-4 h-4 rounded" style={{ backgroundColor: formData.companyColor }}></span>
                      {formData.companyName}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Layout</p>
                    <p className="font-semibold">{LAYOUT_STYLES.find(s => s.id === formData.layoutStyle)?.label}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Format</p>
                    <p className="font-semibold">{(formData.formats || []).map(f => FORMAT_OPTIONS.find(fo => fo.id === f)?.label || f).join(", ")}</p>
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <p className="text-muted-foreground mb-2">Vorteile ({formData.advantages.filter(a => a.trim()).length})</p>
                  <ul className="list-disc list-inside text-sm">
                    {formData.advantages.filter(a => a.trim()).map((adv, i) => (
                      <li key={i}>{adv}</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
              <CardFooter className="flex-col gap-3">
                <Button
                  className="w-full"
                  size="lg"
                  onClick={generateAd}
                  disabled={generating}
                  data-testid="generate-ad-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generiere Vergleichs-Ad...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Vergleichs-Ad generieren
                    </>
                  )}
                </Button>
                
                {/* Progress Bar */}
                {generating && (
                  <div className="w-full space-y-2">
                    <Progress value={generationProgress} className="h-2" />
                    <p className="text-xs text-center text-muted-foreground">
                      {generationProgress}% - Bitte warten...
                    </p>
                  </div>
                )}
              </CardFooter>
            </Card>

            {/* Generated Images Gallery */}
            {generatedImages.length > 0 && (
              <Card className="max-w-4xl mx-auto mt-8" data-testid="generated-images-card">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Generierte Bilder ({generatedImages.length})</span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSaveTemplateDialog(true)}
                        data-testid="save-template-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Als Vorlage speichern
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowTextEditor(true)}
                        data-testid="edit-text-btn"
                      >
                        <Edit3 className="w-4 h-4 mr-2" />
                        Text bearbeiten
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={downloadImage}
                        data-testid="download-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Herunterladen
                      </Button>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    Klicke auf ein Bild um es auszuwählen
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Main selected image */}
                  <div className="relative">
                    <img
                      src={`data:image/png;base64,${generatedImages[selectedImageIndex]?.image}`}
                      alt={`Generated Comparison Ad ${selectedImageIndex + 1}`}
                      className="w-full rounded-lg shadow-lg"
                      data-testid="selected-image"
                    />
                    <Badge className="absolute top-3 left-3">
                      Variation {selectedImageIndex + 1}
                    </Badge>
                  </div>
                  
                  {/* Thumbnail grid for multiple variations */}
                  {generatedImages.length > 1 && (
                    <div className="grid grid-cols-4 gap-3">
                      {generatedImages.map((img, idx) => (
                        <div
                          key={idx}
                          onClick={() => setSelectedImageIndex(idx)}
                          className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                            selectedImageIndex === idx 
                              ? "border-primary ring-2 ring-primary/30" 
                              : "border-transparent hover:border-muted-foreground/50"
                          }`}
                          data-testid={`thumbnail-${idx}`}
                        >
                          <img
                            src={`data:image/png;base64,${img.image}`}
                            alt={`Variation ${idx + 1}`}
                            className="w-full aspect-square object-cover"
                          />
                          {selectedImageIndex === idx && (
                            <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                              <Check className="w-6 h-6 text-primary" />
                            </div>
                          )}
                          <span className="absolute bottom-1 right-1 text-xs bg-black/50 text-white px-1.5 py-0.5 rounded">
                            #{idx + 1}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
                <CardFooter className="flex justify-center gap-4">
                  <Button
                    variant="secondary"
                    onClick={generateAd}
                    disabled={generating}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Neu generieren
                  </Button>
                </CardFooter>
              </Card>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      {/* Progress Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Vergleichs-Ad Generator</h1>
          <Badge variant="outline">Schritt {currentStep} von {STEPS.length}</Badge>
        </div>
        
        {/* Step indicators */}
        <div className="flex items-center gap-2 mb-4">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                  currentStep === step.id
                    ? "bg-primary text-primary-foreground"
                    : currentStep > step.id
                    ? "bg-green-100 text-green-700"
                    : "bg-muted text-muted-foreground"
                }`}
                onClick={() => currentStep > step.id && setCurrentStep(step.id)}
              >
                {currentStep > step.id ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <step.icon className="w-4 h-4" />
                )}
                <span className="text-sm font-medium hidden md:inline">{step.title}</span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 ${currentStep > step.id ? "bg-green-400" : "bg-muted"}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        
        <Progress value={progress} className="h-2" />
      </div>

      {/* Step Content */}
      <div className="max-w-4xl mx-auto">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="max-w-4xl mx-auto mt-8 flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(prev => prev - 1)}
          disabled={currentStep === 1}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        
        {currentStep < STEPS.length && (
          <Button
            onClick={() => setCurrentStep(prev => prev + 1)}
            disabled={!canProceed()}
            data-testid="next-step-btn"
          >
            Weiter
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Text Editor Dialog */}
      <Dialog open={showTextEditor} onOpenChange={setShowTextEditor}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Text im Bild bearbeiten</DialogTitle>
            <DialogDescription>
              Mit KI-Power kannst du Texte direkt im Bild ändern
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Detect Text Button */}
            <Button
              onClick={detectText}
              disabled={isDetecting}
              className="w-full"
              variant="secondary"
              data-testid="detect-text-btn"
            >
              {isDetecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Erkenne Texte...
                </>
              ) : (
                <>
                  <ScanSearch className="w-4 h-4 mr-2" />
                  Texte im Bild erkennen
                </>
              )}
            </Button>

            {/* Detected Texts */}
            {detectedTexts.length > 0 && (
              <div className="space-y-2">
                <Label>Erkannte Texte:</Label>
                <ScrollArea className="h-48 border rounded-lg p-2">
                  {detectedTexts.map((textItem, index) => (
                    <div
                      key={index}
                      className={`p-2 rounded cursor-pointer hover:bg-muted ${
                        selectedText?.text === textItem.text ? "bg-primary/10 border border-primary" : ""
                      }`}
                      onClick={() => {
                        setSelectedText(textItem);
                        setReplacementText(textItem.text);
                      }}
                    >
                      <p className="text-sm font-medium">{textItem.text}</p>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}

            {/* Replace Text Form */}
            {selectedText && (
              <div className="space-y-3 border-t pt-4">
                <div>
                  <Label>Ausgewählter Text:</Label>
                  <p className="text-sm bg-muted p-2 rounded mt-1">{selectedText.text}</p>
                </div>
                <div>
                  <Label htmlFor="newText">Neuer Text:</Label>
                  <Input
                    id="newText"
                    value={replacementText}
                    onChange={(e) => setReplacementText(e.target.value)}
                    placeholder="Neuen Text eingeben..."
                    className="mt-1"
                    data-testid="replacement-text-input"
                  />
                </div>
                <Button
                  onClick={replaceText}
                  disabled={isReplacing || !replacementText.trim()}
                  className="w-full"
                  data-testid="replace-text-btn"
                >
                  {isReplacing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Ersetze mit KI...
                    </>
                  ) : (
                    <>
                      <Type className="w-4 h-4 mr-2" />
                      Text ersetzen
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTextEditor(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={showSaveTemplateDialog} onOpenChange={setShowSaveTemplateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="w-5 h-5" />
              Als Vorlage speichern
            </DialogTitle>
            <DialogDescription>
              Speichere deine aktuellen Einstellungen als wiederverwendbare Vorlage.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="templateName">Vorlagenname</Label>
              <Input
                id="templateName"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="z.B. Pflegekraft Standard"
                className="mt-1"
                data-testid="template-name-input"
              />
            </div>
            
            <div className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
              <p className="font-medium mb-2">Wird gespeichert:</p>
              <ul className="space-y-1 text-xs">
                <li>• Job: {formData.jobTitle || '-'}</li>
                <li>• Firma: {formData.companyName || '-'}</li>
                <li>• {formData.advantages.filter(a => a.trim()).length} Vorteile</li>
                <li>• Layout: {LAYOUT_STYLES.find(s => s.id === formData.layoutStyle)?.label}</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTemplateDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={saveAsTemplate} 
              disabled={savingTemplate || !templateName.trim()}
              data-testid="confirm-save-template-btn"
            >
              {savingTemplate ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Speichern
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComparisonAdGenerator;
